import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, DatabaseIcon, LayoutGridIcon, MonitorPlayIcon, RotateCcwIcon, UsersIcon } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
const FACULTY = [{
  name: 'Dr. Linda Stein Gold',
  role: 'DERMX Chair',
  img: 'https://cdn.magicpatterns.com/uploads/ps6HkGVYtSyFwcnbRi4Njr/pasted-image.png'
}, {
  name: 'Dr. Raj Chovatiya',
  role: 'Panelist',
  img: 'https://cdn.magicpatterns.com/uploads/iLegRZ3zR98E1YeHyrRLPR/pasted-image.png'
}, {
  name: 'Prof. Swen Malte John',
  role: 'Panelist',
  img: 'https://cdn.magicpatterns.com/uploads/5KheWsyKZQcUBHm8B2kq9A/pasted-image.png'
}, {
  name: 'Prof. Ana María Giménez-Arnau',
  role: 'Panelist',
  img: 'https://cdn.magicpatterns.com/uploads/4G5yRarKD4Zo4ZrzxB1WYh/pasted-image.png'
}, {
  name: 'Prof. Xue Chen',
  role: 'Session 3 Guest',
  img: 'https://cdn.magicpatterns.com/uploads/chCdXr9hjMrZocGJRGX2hW/pasted-image.png'
}];
const ENGAGEMENT_FEATURES = [{
  icon: MonitorPlayIcon,
  text: 'Chaptered by KOL topic, so any viewer can jump straight to the moment that matters to them — just like being in the room live'
}, {
  icon: LayoutGridIcon,
  text: 'In-chapter polls & quizzes at natural pause points, for reviewers and first-time viewers alike'
}, {
  icon: BarChart3Icon,
  text: 'Chapter completion tracking — see exactly where the webinar is watched, skipped or dropped'
}, {
  icon: RotateCcwIcon,
  text: 'Overview progress to bring viewers back to unfinished chapters'
}, {
  icon: DatabaseIcon,
  text: 'Every interaction captured as first-party engagement data'
}];
export function Slide04Engagement({
  index
}: SlideProps) {
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--surface)] p-[clamp(2rem,4vw,4rem)] flex flex-col" data-id="element-115" data-mp="3a655q9dlfd5r">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1rem,1.6vw,1.5rem)]" data-id="element-116" data-mp="357bs8gyb3m5l">
        <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.5rem,1vw,1rem)]" data-id="element-117" data-mp="3h8a4445rdwk9">
          2. The Evergreen Webinar Snak
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(2rem,3.5vw,3rem)] leading-[1.1]" data-id="element-118" data-mp="3r6823jdf77q2">
          Make every viewer feel like{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-119" data-mp="2yj3s0zzh037f">
            they&apos;re in the room
          </span>
          .
        </h2>
      </motion.div>

      {/* What is it */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.28,
      duration: 0.5
    }} className="mb-[clamp(1rem,1.6vw,1.5rem)] flex-shrink-0 rounded-2xl bg-white p-[clamp(1.1rem,1.6vw,1.5rem)] shadow-sm border-t-8 border-[var(--brand-green)]" data-id="element-120" data-mp="389gbeovjtqbz">
        <div className="flex items-start gap-3" data-id="element-121" data-mp="wyauyldccxla">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--brand-green)]/10" data-id="element-122" data-mp="wvx1ggyb7tfu">
            <UsersIcon className="h-[1.125rem] w-[1.125rem] text-[var(--brand-green)]" data-id="element-123" data-mp="nxcmnqvve3ww" />
          </span>
          <p className="text-[clamp(0.9rem,1.05vw,1.05rem)] leading-snug text-[var(--fg)]" data-id="element-124" data-mp="iyi7r010wzi2">
            <span className="font-bold" data-id="element-125" data-mp="dz5b5nkcoe2a">What it is: </span>
            the full DERMX November 2026 panel — chaired live by Dr. Linda
            Stein Gold with all 4 expert panelists — rebuilt as an
            interactive, on-demand Snak. Whether a viewer missed the live
            sessions or wants to revisit a moment, the chaptered format and
            built-in interactivity bring them into the experience as if they
            were there.
          </p>
        </div>
      </motion.div>

      {/* Faculty strip */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.35,
      duration: 0.5
    }} className="flex items-center gap-[clamp(1rem,2vw,2rem)] mb-[clamp(1rem,1.6vw,1.5rem)] flex-shrink-0" data-id="element-126" data-mp="3meoyy729549y">
        <span className="text-[var(--muted)] text-[clamp(0.7rem,0.9vw,0.85rem)] font-semibold uppercase tracking-wider flex-shrink-0" data-id="element-127" data-mp="1vcrkr6020fdz">
          DERMX November 2026 Faculty
        </span>
        <div className="flex items-center gap-[clamp(0.75rem,1.5vw,1.5rem)] flex-wrap" data-id="element-128" data-mp="1lj0z08vbaj1z">
          {FACULTY.map(f => <div key={f.name} className="flex items-center gap-2" data-id="element-129" data-mp="1ck5qgq4sowvi">
              <img src={f.img} alt={f.name} className="w-[clamp(2rem,3vw,2.75rem)] h-[clamp(2rem,3vw,2.75rem)] rounded-full object-cover border-2 border-white shadow-sm" data-id="element-130" data-mp="1cicgp1yvxwja" />
              <div className="leading-tight" data-id="element-131" data-mp="2s1fnkv377w2t">
                <p className="text-[var(--fg)] font-semibold text-[clamp(0.75rem,0.95vw,0.9rem)]" data-id="element-132" data-mp="2s1pb8c0rtr5c">
                  {f.name}
                </p>
                <p className="text-[var(--muted)] text-[clamp(0.65rem,0.8vw,0.75rem)]" data-id="element-133" data-mp="lcnpanynqu7h">
                  {f.role}
                </p>
              </div>
            </div>)}
        </div>
      </motion.div>

      <div className="flex-1 grid grid-cols-12 gap-[clamp(1.5rem,3vw,3rem)] min-h-0" data-id="element-134" data-mp="v38mc6yonqoa">
        {/* Engagement features */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.45,
        duration: 0.6
      }} className="col-span-7 bg-white rounded-2xl p-[clamp(1.5rem,2.2vw,2.2rem)] shadow-sm flex flex-col" data-id="element-135" data-mp="qb8dfp0mxnfk">
          <h3 className="text-[var(--brand-green)] font-bold text-[clamp(1.1rem,1.5vw,1.4rem)] mb-4" data-id="element-136" data-mp="1e1um9iigrbqm">
            What it brings — engagement
          </h3>
          <ul className="space-y-3.5 flex-1" data-id="element-137" data-mp="190f3jx3rdhbe">
            {ENGAGEMENT_FEATURES.map((f, i) => {
            const Icon = f.icon;
            return <li key={i} className="flex items-start gap-3" data-id="element-138" data-mp="3auwezq6nyx1a" data-mpi={i}>
                  <span className="w-8 h-8 rounded-lg bg-[var(--brand-green)]/10 flex items-center justify-center flex-shrink-0" data-id="element-139" data-mp="1eqzryzpyij0z">
                    <Icon className="w-4 h-4 text-[var(--brand-green)]" data-id="element-140" data-mp="1jqui311wr4kz" />
                  </span>
                  <span className="text-[var(--fg)] text-[clamp(0.9rem,1.05vw,1.05rem)] leading-snug" data-id="element-141" data-mp="1nqd3rz1jzjov">
                    {f.text}
                  </span>
                </li>;
          })}
          </ul>
        </motion.div>

        {/* Data -> spinoff campaigns */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.55,
        duration: 0.6
      }} className="col-span-5 bg-[var(--brand-green)] rounded-2xl p-[clamp(1.5rem,2.2vw,2.2rem)] shadow-xl text-white flex flex-col justify-center" data-id="element-142" data-mp="22o5fsh7w5pyb">
          <p className="text-[var(--brand-green-lighter)] font-semibold uppercase tracking-wider text-sm mb-2" data-id="element-143" data-mp="2gh4vaj7obk5v">
            What it brings — data
          </p>
          <p className="text-white/90 text-[clamp(0.95rem,1.15vw,1.1rem)] leading-relaxed" data-id="element-144" data-mp="2vj3nlcydpymf">
            Poll results, completion patterns and drop-off points become a
            ready-made brief for follow-up medical education, targeted
            reminders and spinoff campaigns.
          </p>
        </motion.div>
      </div>

      {/* Bottom statement */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8,
      duration: 0.6
    }} className="flex justify-between items-end mt-[clamp(1rem,1.5vw,1.5rem)] flex-shrink-0" data-id="element-145" data-mp="2hkwxiizo92xb">
        <p className="text-[var(--fg)] text-[clamp(1rem,1.25vw,1.25rem)] font-semibold" data-id="element-146" data-mp="34js0g4c1dsxq">
          On-demand doesn&apos;t mean distant — every viewer gets the live
          feeling, on their own time.
        </p>
      </motion.div>
    </div>;
}